import React, { useState, useEffect, useRef } from 'react';
import { getFacturas, createFactura, updateFactura, anularFactura, cambiarEstadoEnvio, cambiarEstadoPago, createPago, deletePago, getClientes, getTasaCambio, updateTasaCambio, getFurgones } from '../services/api';
import PrintFactura from '../components/PrintFactura';
import PrintEtiqueta from '../components/PrintEtiqueta';

const ESTADOS_ENVIO = ['Recibido en Florida', 'Cargado en Furgón', 'En Tránsito', 'Llegó a República Dominicana', 'Entregado'];
const ESTADOS_PAGO = ['Pendiente', 'Abono Parcial', 'Pagada'];

const PROVINCIAS_RD = [
  'Distrito Nacional',
  'Santo Domingo — Los Mina', 'Santo Domingo — Villa Mella', 'Santo Domingo — Los Alcarrizos',
  'Santo Domingo — San Luis', 'Santo Domingo — Sabana Perdida', 'Santo Domingo — Herrera',
  'Santo Domingo — Invivienda', 'Santo Domingo — Villa Duarte', 'Santo Domingo — Boca Chica',
  'Azua', 'Bahoruco', 'Barahona', 'Dajabón', 'Duarte', 'Elías Piña', 'El Seibo',
  'Espaillat', 'Hato Mayor', 'Independencia', 'La Altagracia', 'La Romana',
  'La Vega', 'María Trinidad Sánchez', 'Monseñor Nouel', 'Monte Cristi',
  'Monte Plata', 'Pedernales', 'Peravia', 'Puerto Plata', 'Samaná',
  'San Cristóbal', 'San José de Ocoa', 'San Juan', 'San Pedro de Macorís',
  'Sánchez Ramírez', 'Santiago', 'Santiago Rodríguez', 'Valverde',
];

const fmt = (monto, moneda) => {
  if (moneda === 'DOP') return `RD$${Number(monto).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;
  return `$${Number(monto).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

const fmtDOP = (monto) => `RD$${Number(monto).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;

const badgeStyle = (estado) => {
  const map = {
    'Pendiente': { bg: '#fef3c7', color: '#d97706' },
    'Abono Parcial': { bg: '#dbeafe', color: '#1d4ed8' },
    'Pagada': { bg: '#dcfce7', color: '#16a34a' },
    'Anulada': { bg: '#fee2e2', color: '#cc0000' },
    'Recibido en Florida': { bg: '#f1f5f9', color: '#64748b' },
    'Cargado en Furgón': { bg: '#dbeafe', color: '#1d4ed8' },
    'En Tránsito': { bg: '#fef3c7', color: '#d97706' },
    'Llegó a República Dominicana': { bg: '#ede9fe', color: '#7c3aed' },
    'Entregado': { bg: '#dcfce7', color: '#16a34a' },
  };
  const s = map[estado] || { bg: '#f1f5f9', color: '#64748b' };
  return { background: s.bg, color: s.color, padding: '3px 8px', borderRadius: 100, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };
};

const emptyDetalle = () => ({ cajas: 0, fornitura: 0, tanques: 0, vehiculo: 0, otro: '', descripcion: '', cantidad: 1, precioUnitario: 0, total: 0 });

// Input con autocompletado de clientes
function ClienteAutocomplete({ value, onChange, onSelectCliente, placeholder, clientes }) {
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrar, setMostrar] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMostrar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (val) => {
    onChange(val);
    if (val.length >= 2) {
      const filtradas = clientes.filter(c => c.nombreCompleto?.toLowerCase().includes(val.toLowerCase())).slice(0, 5);
      setSugerencias(filtradas);
      setMostrar(filtradas.length > 0);
    } else {
      setMostrar(false);
    }
  };

  const seleccionar = (cliente) => {
    onSelectCliente(cliente);
    setMostrar(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input type="text" placeholder={placeholder} value={value}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (sugerencias.length > 0) setMostrar(true); }}
        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
      {mostrar && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, boxShadow: '0 8px 20px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: 200, overflowY: 'auto' }}>
          {sugerencias.map(c => (
            <div key={c.id} onClick={() => seleccionar(c)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.nombreCompleto}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.telefono} · {c.ciudad}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Widget de tasa de cambio
function TasaCambioWidget({ tasaCambio, onActualizar }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(tasaCambio?.tasaCambioActual || 59);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValor(tasaCambio?.tasaCambioActual || 59); }, [tasaCambio]);

  const guardar = async () => {
    setSaving(true);
    try {
      await updateTasaCambio({ tasaCambio: parseFloat(valor) });
      onActualizar();
      setEditando(false);
    } catch { alert('Error al actualizar la tasa de cambio'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#0d2461' }}>Tasa de Cambio (USD → DOP):</span>
      {editando ? (
        <>
          <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} autoFocus
            style={{ width: 90, padding: '5px 8px', border: '1.5px solid #0d2461', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          <button onClick={guardar} disabled={saving}
            style={{ padding: '5px 12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? '...' : '✓ Guardar'}
          </button>
          <button onClick={() => { setEditando(false); setValor(tasaCambio?.tasaCambioActual || 59); }}
            style={{ padding: '5px 12px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
        </>
      ) : (
        <>
          <strong style={{ fontSize: 15, color: '#0d2461' }}>RD${tasaCambio?.tasaCambioActual?.toFixed(2) || '59.00'}</strong>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            (Actualizada: {tasaCambio?.fechaActualizacion ? new Date(tasaCambio.fechaActualizacion).toLocaleDateString('es-DO') : '—'})
          </span>
          <button onClick={() => setEditando(true)}
            style={{ padding: '5px 12px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>✏️ Actualizar</button>
        </>
      )}
    </div>
  );
}

// Widget de furgón activo
function FurgonActivoWidget({ furgonActivo, furgones, onSeleccionar, onSalir }) {
  return furgonActivo ? (
    <div style={{ background: '#0d2461', color: 'white', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>🚛 Trabajando con Furgón #{furgonActivo}</span>
      <button onClick={onSalir}
        style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>
        Salir del modo furgón
      </button>
    </div>
  ) : (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#0d2461' }}>Seleccionar furgón para trabajar:</span>
      <select onChange={e => e.target.value && onSeleccionar(e.target.value)} value=""
        style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
        <option value="">-- Elegir furgón --</option>
        {furgones.map(f => <option key={f.id} value={f.numero}>Furgón #{f.numero}</option>)}
      </select>
    </div>
  );
}

function ModalFactura({ factura, onClose, onSaved, tasaCambioDefault, furgonDefault }) {
  const isEdit = !!factura?.id;
  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState({
    numero: '',
    fecha: new Date().toISOString().split('T')[0],
    moneda: 'USD', tasaCambio: tasaCambioDefault || 59,
    furgonNumero: furgonDefault || '', furgonCode: '',
    lugarPago: 'RD',
    remNombre: '', remTelefono: '', remDireccion: '', remCiudad: '',
    desNombre: '', desTelefono: '', desDireccion: '', desCiudad: '',
    deposito: 0, observaciones: '',
    detalles: [emptyDetalle()],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getClientes().then(res => setClientes(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      setForm({
        numero: factura.numero,
        fecha: factura.fecha?.split('T')[0] || '',
        moneda: factura.moneda, tasaCambio: factura.tasaCambio,
        furgonNumero: factura.furgonNumero, furgonCode: factura.furgonCode || '',
        lugarPago: factura.lugarPago || 'RD',
        remNombre: factura.remNombre, remTelefono: factura.remTelefono,
        remDireccion: factura.remDireccion, remCiudad: factura.remCiudad,
        desNombre: factura.desNombre, desTelefono: factura.desTelefono,
        desDireccion: factura.desDireccion, desCiudad: factura.desCiudad,
        deposito: factura.deposito, observaciones: factura.observaciones || '',
        detalles: factura.detalles?.length ? factura.detalles : [emptyDetalle()],
      });
    }
  }, [factura]);

  const setDetalle = (i, field, val) => {
    const d = [...form.detalles];
    d[i] = { ...d[i], [field]: val };
    if (field === 'cantidad' || field === 'precioUnitario') {
      d[i].total = (parseFloat(d[i].cantidad) || 0) * (parseFloat(d[i].precioUnitario) || 0);
    }
    setForm({ ...form, detalles: d });
  };

  const addDetalle = () => setForm({ ...form, detalles: [...form.detalles, emptyDetalle()] });
  const removeDetalle = (i) => setForm({ ...form, detalles: form.detalles.filter((_, idx) => idx !== i) });

  const seleccionarRemitente = (cliente) => {
    setForm({ ...form, remNombre: cliente.nombreCompleto, remTelefono: cliente.telefono, remDireccion: cliente.direccion, remCiudad: cliente.ciudad });
  };

  const seleccionarDestinatario = (cliente) => {
    setForm({ ...form, desNombre: cliente.nombreCompleto, desTelefono: cliente.telefono, desDireccion: cliente.direccion, desCiudad: cliente.ciudad });
  };

  const subtotal = form.detalles.reduce((s, d) => s + (d.total || 0), 0);
  const balance = subtotal - (parseFloat(form.deposito) || 0);
  const equivalenteDOP = form.moneda === 'USD' ? subtotal * (parseFloat(form.tasaCambio) || 0) : null;

  const guardar = async () => {
    if (!form.remNombre || !form.desNombre) { setError('Remitente y destinatario son requeridos'); return; }
    setSaving(true); setError('');
    try {
      const data = { ...form, detalles: form.detalles };
      if (isEdit) await updateFactura(factura.id, data);
      else await createFactura(data);
      onSaved();
    } catch (err) { setError(err?.response?.data?.message || 'Error al guardar la factura.'); }
    finally { setSaving(false); }
  };

  const inp = (field, placeholder, type = 'text') => (
    <input type={type} placeholder={placeholder} value={form[field]}
      onChange={e => setForm({ ...form, [field]: e.target.value })}
      style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
  );

  const numInp = (i, field) => (
    <input type="number" min="0" value={form.detalles[i][field]}
      onChange={e => setDetalle(i, field, e.target.value)}
      style={{ width: 55, padding: '4px 6px', border: 'none', fontSize: 13, fontFamily: 'inherit', outline: 'none', textAlign: 'center' }} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 1000, boxShadow: '0 25px 50px rgba(0,0,0,0.25)', margin: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0d2461' }}>{isEdit ? `Editar ${factura.numero}` : 'Nueva Factura'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        <div style={{ padding: 24, maxHeight: '75vh', overflowY: 'auto' }}>
          {error && <div style={{ background: '#fee2e2', color: '#cc0000', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>⚠️ {error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: isEdit ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {isEdit && (
              <div><label style={lbl}>Número Factura</label><div style={{ marginTop: 5 }}>{inp('numero', 'Ej: FAC-000001')}</div></div>
            )}
            <div><label style={lbl}>Fecha</label><div style={{ marginTop: 5 }}>{inp('fecha', '', 'date')}</div></div>
            <div>
              <label style={lbl}>Moneda</label>
              <select value={form.moneda} onChange={e => setForm({ ...form, moneda: e.target.value })}
                style={{ width: '100%', marginTop: 5, padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                <option value="USD">USD — Dólares</option>
                <option value="DOP">DOP — Pesos</option>
              </select>
            </div>
            <div><label style={lbl}>Furgón No.</label><div style={{ marginTop: 5 }}>{inp('furgonNumero', 'Ej: 147')}</div></div>
            <div>
              <label style={lbl}>Lugar de Pago</label>
              <select value={form.lugarPago} onChange={e => setForm({ ...form, lugarPago: e.target.value })}
                style={{ width: '100%', marginTop: 5, padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                <option value="RD">Pago en RD</option>
                <option value="USA">Pago en USA</option>
              </select>
            </div>
          </div>

          {form.moneda === 'USD' && (
            <div style={{ marginBottom: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Tasa Cambio Aplicada:</label>
              <input type="number" step="0.01" value={form.tasaCambio} onChange={e => setForm({ ...form, tasaCambio: e.target.value })}
                style={{ width: 90, padding: '5px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
              <span style={{ fontSize: 12, color: '#64748b' }}>Equivalente aprox: <strong style={{ color: '#0d2461' }}>{fmtDOP(equivalenteDOP || 0)}</strong></span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: 16, position: 'relative' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0d2461', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>📤 Remitente (ENVÍA)</div>
              <div style={{ marginBottom: 8 }}>
                <ClienteAutocomplete value={form.remNombre} placeholder="Nombre"
                  onChange={val => setForm({ ...form, remNombre: val })}
                  onSelectCliente={seleccionarRemitente}
                  clientes={clientes} />
              </div>
              <div style={{ marginBottom: 8 }}>{inp('remTelefono', 'Teléfono')}</div>
              <div style={{ marginBottom: 8 }}>{inp('remDireccion', 'Dirección')}</div>
              <div style={{ marginBottom: 8 }}>{inp('remCiudad', 'Ciudad y Estado')}</div>
            </div>
            <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: 16, position: 'relative' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0d2461', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>📥 Destinatario (RECIBE)</div>
              <div style={{ marginBottom: 8 }}>
                <ClienteAutocomplete value={form.desNombre} placeholder="Nombre"
                  onChange={val => setForm({ ...form, desNombre: val })}
                  onSelectCliente={seleccionarDestinatario}
                  clientes={clientes} />
              </div>
              <div style={{ marginBottom: 8 }}>{inp('desTelefono', 'Teléfono')}</div>
              <div style={{ marginBottom: 8 }}>{inp('desDireccion', 'Dirección')}</div>
              <div style={{ marginBottom: 8 }}>
                <select value={form.desCiudad} onChange={e => setForm({ ...form, desCiudad: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}>
                  <option value="">Selecciona provincia/sector...</option>
                  {PROVINCIAS_RD.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0d2461', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>📦 Artículos</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Cajas', 'Fornitura', 'Tanques', 'Vehículo', 'Otro', 'Descripción', 'Can.', 'Precio Unit.', 'Total', ''].map(h => (
                      <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.detalles.map((d, i) => (
                    <tr key={i}>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>{numInp(i, 'cajas')}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>{numInp(i, 'fornitura')}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>{numInp(i, 'tanques')}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>{numInp(i, 'vehiculo')}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>
                        <input value={d.otro} onChange={e => setDetalle(i, 'otro', e.target.value)} placeholder="Otro..."
                          style={{ width: 90, padding: '4px 6px', border: 'none', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                      </td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>
                        <input value={d.descripcion} onChange={e => setDetalle(i, 'descripcion', e.target.value)} placeholder="Descripción"
                          style={{ width: '100%', minWidth: 140, padding: '4px 6px', border: 'none', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
                      </td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>
                        <input type="number" min="1" value={d.cantidad} onChange={e => setDetalle(i, 'cantidad', e.target.value)}
                          style={{ width: 55, padding: '4px 6px', border: 'none', fontSize: 13, fontFamily: 'inherit', outline: 'none', textAlign: 'center' }} />
                      </td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>
                        <input type="number" min="0" step="0.01" value={d.precioUnitario} onChange={e => setDetalle(i, 'precioUnitario', e.target.value)}
                          style={{ width: 80, padding: '4px 6px', border: 'none', fontSize: 13, fontFamily: 'inherit', outline: 'none', textAlign: 'right' }} />
                      </td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '4px 8px', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(d.total || 0, form.moneda)}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: 4, textAlign: 'center' }}>
                        <button onClick={() => removeDetalle(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8' }}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={8} style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, border: '1px solid #e2e8f0', background: '#f8fafc' }}>SUBTOTAL</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, border: '1px solid #e2e8f0', background: '#f8fafc', textAlign: 'right' }}>{fmt(subtotal, form.moneda)}</td>
                    <td style={{ border: '1px solid #e2e8f0', background: '#f8fafc' }}></td>
                  </tr>
                  <tr>
                    <td colSpan={8} style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, border: '1px solid #e2e8f0' }}>DEPÓSITO</td>
                    <td style={{ border: '1px solid #e2e8f0', padding: 4 }}>
                      <input type="number" min="0" value={form.deposito} onChange={e => setForm({ ...form, deposito: e.target.value })}
                        style={{ width: '100%', padding: '4px 8px', border: 'none', fontSize: 13, fontFamily: 'inherit', outline: 'none', textAlign: 'right' }} />
                    </td>
                    <td style={{ border: '1px solid #e2e8f0' }}></td>
                  </tr>
                  <tr>
                    <td colSpan={8} style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, border: '1px solid #e2e8f0', background: '#f8fafc' }}>BALANCE</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: balance > 0 ? '#cc0000' : '#16a34a', border: '1px solid #e2e8f0', background: '#f8fafc', textAlign: 'right' }}>{fmt(balance, form.moneda)}</td>
                    <td style={{ border: '1px solid #e2e8f0', background: '#f8fafc' }}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <button onClick={addDetalle} style={{ marginTop: 10, padding: '6px 14px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>+ Agregar Artículo</button>
          </div>

          <div>
            <label style={lbl}>Observaciones</label>
            <textarea value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} placeholder="Notas adicionales..."
              style={{ width: '100%', marginTop: 5, padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', minHeight: 70, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', background: 'white', color: '#0d2461', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancelar</button>
          <button onClick={guardar} disabled={saving} style={{ padding: '9px 18px', background: saving ? '#94a3b8' : '#0d2461', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            {saving ? 'Guardando...' : '💾 Guardar Factura'}
          </button>
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' };

function ModalPago({ factura, onClose, onSaved }) {
  const [form, setForm] = useState({ monto: '', moneda: factura.moneda, nota: '', fecha: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pagos, setPagos] = useState(factura.pagos || []);

  const total = factura.detalles?.reduce((s, d) => s + d.total, 0) || 0;
  const pagado = (pagos.reduce((s, p) => s + p.monto, 0) || 0) + (factura.deposito || 0);
  const balance = total - pagado;

  const guardar = async () => {
    if (!form.monto || parseFloat(form.monto) <= 0) { setError('Ingresa un monto válido'); return; }
    setSaving(true); setError('');
    try {
      await createPago({ facturaId: factura.id, monto: parseFloat(form.monto), moneda: form.moneda, nota: form.nota, fecha: form.fecha });
      onSaved();
    } catch { setError('Error al registrar el pago.'); }
    finally { setSaving(false); }
  };

  const eliminarPago = async (pagoId) => {
    if (!window.confirm('¿Eliminar este pago? Esta acción no se puede deshacer.')) return;
    try {
      await deletePago(pagoId);
      setPagos(prev => prev.filter(p => p.id !== pagoId));
      onSaved(true);
    } catch { alert('Error al eliminar el pago. Verifica que tengas permisos de administrador.'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0d2461' }}>Pagos — {factura.numero}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ padding: 24, maxHeight: '75vh', overflowY: 'auto' }}>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 14 }}>
            <strong>{factura.numero}</strong> — {factura.remNombre}<br />
            <span style={{ color: '#64748b' }}>Total: <strong>{fmt(total, factura.moneda)}</strong> | Pagado: <strong style={{ color: '#16a34a' }}>{fmt(pagado, factura.moneda)}</strong> | Balance: <strong style={{ color: '#cc0000' }}>{fmt(balance, factura.moneda)}</strong></span>
          </div>

          {pagos.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Pagos Registrados</label>
              <div style={{ marginTop: 6, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                {pagos.map(p => (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                    <div>
                      <strong>{fmt(p.monto, p.moneda)}</strong>
                      <span style={{ color: '#94a3b8', marginLeft: 8 }}>{new Date(p.fecha).toLocaleDateString('es-DO')}</span>
                      {p.nota && <div style={{ color: '#94a3b8', fontSize: 11 }}>{p.nota}</div>}
                    </div>
                    <button onClick={() => eliminarPago(p.id)} title="Eliminar pago"
                      style={{ padding: '4px 8px', background: '#fee2e2', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🗑️</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div style={{ background: '#fee2e2', color: '#cc0000', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>⚠️ {error}</div>}

          <label style={lbl}>Registrar Nuevo Pago</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6, marginBottom: 12 }}>
            <div>
              <input type="number" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} placeholder="Monto"
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <select value={form.moneda} onChange={e => setForm({ ...form, moneda: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                <option value="USD">USD</option><option value="DOP">DOP</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })}
              style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <textarea value={form.nota} onChange={e => setForm({ ...form, nota: e.target.value })} placeholder="Nota (opcional)..."
              style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', minHeight: 60, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', background: 'white', color: '#0d2461', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cerrar</button>
          <button onClick={guardar} disabled={saving} style={{ padding: '9px 18px', background: saving ? '#94a3b8' : '#16a34a', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
            {saving ? 'Guardando...' : '✅ Registrar Pago'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Facturas() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalFactura, setModalFactura] = useState(false);
  const [modalPago, setModalPago] = useState(false);
  const [printFactura, setPrintFactura] = useState(null);
  const [printEtiqueta, setPrintEtiqueta] = useState(null);
  const [editando, setEditando] = useState(null);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCiudad, setFiltroCiudad] = useState('');
  const [furgonActivo, setFurgonActivo] = useState(() => localStorage.getItem('furgonActivo') || '');
  const [furgones, setFurgones] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [tasaCambio, setTasaCambio] = useState(null);
  const PAGE_SIZE = 20;

  useEffect(() => { cargar(); }, [page, filtroEstado, filtroCiudad, furgonActivo]);
  useEffect(() => { cargarTasaCambio(); cargarFurgones(); }, []);

  const cargar = (silent = false) => {
    if (!silent) setLoading(true);
    getFacturas(page, PAGE_SIZE, buscar, filtroEstado, filtroCiudad, furgonActivo)
      .then(res => {
        setFacturas(res.data.data);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  const cargarTasaCambio = () => {
    getTasaCambio().then(res => setTasaCambio(res.data)).catch(() => {});
  };

  const cargarFurgones = () => {
    getFurgones().then(res => setFurgones(res.data || [])).catch(() => {});
  };

  const seleccionarFurgonActivo = (numero) => {
    setFurgonActivo(numero);
    localStorage.setItem('furgonActivo', numero);
    setPage(1);
  };

  const salirModoFurgon = () => {
    setFurgonActivo('');
    localStorage.removeItem('furgonActivo');
    setPage(1);
  };

  const handleBuscar = () => {
    setPage(1);
    cargar();
  };

  const handleAnular = async (f) => {
    const motivo = prompt('Motivo de anulación:');
    if (!motivo) return;
    try { await anularFactura(f.id, { motivo }); cargar(true); } catch { alert('Error al anular'); }
  };

  const handleCambiarEstado = async (f, estado) => {
    try { await cambiarEstadoEnvio(f.id, { estado }); cargar(true); } catch { alert('Error al cambiar estado'); }
  };

  const handleCambiarEstadoPago = async (f, estadoPago) => {
    try { await cambiarEstadoPago(f.id, { estadoPago }); cargar(true); } catch { alert('Error al cambiar estado de pago'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Cargando...</div>;
  if (printFactura) return <PrintFactura factura={printFactura} onClose={() => setPrintFactura(null)} />;
  if (printEtiqueta) return <PrintEtiqueta factura={printEtiqueta} onClose={() => setPrintEtiqueta(null)} />;
  return (
    <div>
      <FurgonActivoWidget furgonActivo={furgonActivo} furgones={furgones}
        onSeleccionar={seleccionarFurgonActivo} onSalir={salirModoFurgon} />

      <TasaCambioWidget tasaCambio={tasaCambio} onActualizar={cargarTasaCambio} />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <input placeholder="Buscar factura, cliente..." value={buscar}
          onChange={e => setBuscar(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleBuscar()}
          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', flex: 1, minWidth: 200 }} />
        <select value={filtroEstado} onChange={e => { setFiltroEstado(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
          <option value="">Todos los estados</option>
          <option>Pendiente</option><option>Abono Parcial</option><option>Pagada</option><option>Anulada</option>
        </select>
        <select value={filtroCiudad} onChange={e => { setFiltroCiudad(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
          <option value="">Todas las provincias/sectores</option>
          {PROVINCIAS_RD.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button onClick={handleBuscar}
          style={{ padding: '8px 16px', background: '#0d2461', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>🔍 Buscar</button>
        <button onClick={() => { setBuscar(''); setFiltroEstado(''); setFiltroCiudad(''); setPage(1); cargar(); }}
          style={{ padding: '8px 14px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Limpiar</button>
      </div>

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Facturas ({total})</h3>
          <button onClick={() => { setEditando(null); setModalFactura(true); }}
            style={{ padding: '8px 16px', background: '#0d2461', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Nueva Factura
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Factura', 'Fecha', 'Remitente', 'Destinatario', 'Furgón', 'Lugar Pago', 'Total', 'Balance', 'Estado Pago', 'Estado Envío', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {facturas.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No hay facturas</td></tr>
              ) : facturas.map(f => {
                const total = f.detalles?.reduce((s, d) => s + d.total, 0) || 0;
                const pagado = (f.pagos?.reduce((s, p) => s + p.monto, 0) || 0) + (f.deposito || 0);
                const balance = total - pagado;
                return (
                  <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                      {f.numero}
                      {f.anulada && <><br /><span style={badgeStyle('Anulada')}>ANULADA</span></>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{new Date(f.fecha).toLocaleDateString('es-DO')}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{f.remNombre}<br /><span style={{ fontSize: 11, color: '#94a3b8' }}>{f.remCiudad}</span></td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{f.desNombre}<br /><span style={{ fontSize: 11, color: '#94a3b8' }}>{f.desCiudad}</span></td>
                    <td style={{ padding: '12px 16px' }}><span style={{ background: '#dbeafe', color: '#0d2461', padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 600 }}>#{f.furgonNumero}</span></td>
                    <td style={{ padding: '12px 16px', fontSize: 12 }}>{f.lugarPago === 'USA' ? '🇺🇸 USA' : '🇩🇴 RD'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600 }}>
                      {fmt(total, f.moneda)}
                      {f.moneda === 'USD' && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>≈ {fmtDOP(total * (f.tasaCambio || 59))}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: balance > 0 ? '#cc0000' : '#16a34a' }}>
                      {fmt(balance, f.moneda)}
                      {f.moneda === 'USD' && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>≈ {fmtDOP(balance * (f.tasaCambio || 59))}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {f.anulada ? (
                        <span style={badgeStyle(f.estadoPago)}>{f.estadoPago}</span>
                      ) : (
                        <select value={f.estadoPago} onChange={e => handleCambiarEstadoPago(f, e.target.value)}
                          style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', background: 'white', cursor: 'pointer' }}>
                          {ESTADOS_PAGO.map(e => <option key={e}>{e}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <select value={f.estadoEnvio} onChange={e => handleCambiarEstado(f, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', background: 'white', cursor: 'pointer' }}>
                        {ESTADOS_ENVIO.map(e => <option key={e}>{e}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => { setEditando(f); setModalFactura(true); }} title="Editar"
                          style={{ padding: '5px 8px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✏️</button>
                        <button onClick={() => { setFacturaSeleccionada(f); setModalPago(true); }} title="Pago"
                          style={{ padding: '5px 8px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>💰</button>
                        <button onClick={() => setPrintFactura(f)} title="Imprimir Factura"
                          style={{ padding: '5px 8px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🖨️</button>
                        <button onClick={() => setPrintEtiqueta(f)} title="Imprimir Etiqueta"
                          style={{ padding: '5px 8px', background: '#f1f5f9', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🏷️</button>
                        {!f.anulada && (
                          <button onClick={() => handleAnular(f)} title="Anular"
                            style={{ padding: '5px 8px', background: '#fee2e2', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🚫</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Página {page} de {totalPages} — {total} facturas en total
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setPage(1)} disabled={page === 1}
                style={{ padding: '6px 10px', background: page === 1 ? '#f1f5f9' : 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#94a3b8' : '#0d2461' }}>«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
                style={{ padding: '6px 12px', background: page === 1 ? '#f1f5f9' : 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#94a3b8' : '#0d2461' }}>‹ Anterior</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages}
                style={{ padding: '6px 12px', background: page === totalPages ? '#f1f5f9' : '#0d2461', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: page === totalPages ? 'not-allowed' : 'pointer', color: page === totalPages ? '#94a3b8' : 'white', fontWeight: 600 }}>Siguiente ›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                style={{ padding: '6px 10px', background: page === totalPages ? '#f1f5f9' : 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: page === totalPages ? 'not-allowed' : 'pointer', color: page === totalPages ? '#94a3b8' : '#0d2461' }}>»</button>
            </div>
          </div>
        )}
      </div>

      {modalFactura && (
        <ModalFactura factura={editando} tasaCambioDefault={tasaCambio?.tasaCambioActual} furgonDefault={furgonActivo}
          onClose={() => setModalFactura(false)}
          onSaved={() => { setModalFactura(false); cargar(); }} />
      )}
      {modalPago && facturaSeleccionada && (
        <ModalPago factura={facturaSeleccionada}
          onClose={() => setModalPago(false)}
          onSaved={() => { setModalPago(false); cargar(true); }} />
      )}
    </div>
  );
}

export default Facturas;